import { Button, Heading4Med, Placeholder, FileExplorer } from '@hai/ui-react';
import {
  HaiDataTableColumnType,
  HaiSortDirection,
  IFileData,
  IHaiColumnSort,
  /* IFileExplorerActionItem,*/
} from '@hai/ui-react/dist/types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutable } from 'src/js/hook/use-mutable';
import { useStateWatch, useWatchObject } from 'src/js/store/use-watch';
import { createListStore, ListSelector } from 'src/js/util/listSelector';
import { LogoModel, LogoSettingsViewModel } from './logos-models';
import { LogoImportModal } from './logos-import-modal';
import { LogoConfigureModal } from './logo-configure-modal';
import { LogoSettingsForm } from './logos-settings';
import { deleteLogo, fetchLogos, fetchLogoSettings, logoSettingsStore, logoStore, uploadLogo } from './logos-store';
import { v4 as uuidv4 } from 'uuid';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { generateDataAuto } from 'src/js/util/automation';
import moment from 'moment';
import { constant } from 'src/js/util/constant';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { NotificationVariant } from 'src/js/notification/notification';
import systemInfo from 'src/js/data/systemInfo';
import { useNavigate } from 'react-router-dom';
import useLocalStorageWithState from 'src/js/component/useLocalStorageWithState';
import { useStores } from 'src/js/hook/use-stores';
import { triggerEvent, watchEvent } from 'src/js/events';
import { defaultLocaleInfo } from '@hai/ui-react/dist/components/FileExplorer/FileExplorer-helpers';
import { useLocalStorage } from 'usehooks-ts';
import { GenericDeleteModal } from 'src/js/component/generic-delete-modal';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';

interface Props {}

export const LogosPage = (_props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionStore } = useStores();

  const [loading, setLoading] = useState(true);

  const [logos, setLogos] = useState(logoStore);
  useWatchObject(logoStore, setLogos);

  const [logoSettings, setLogoSettings] = useState(logoSettingsStore);
  useWatchObject(logoSettingsStore, setLogoSettings);

  const sortStateDefault: IHaiColumnSort = {
    fieldKey: 'name',
    order: HaiSortDirection.ASC,
  };
  const sortStateKey = `mx4e:logos:sortState:${sessionStore.username}`;
  const [sortState, setSortState] = useLocalStorage(sortStateKey, sortStateDefault);
  const [sortStateState] = useState(sortState);

  const interval = 5000;
  useAdaptiveFetch(() => fetchLogoSettings(t), interval);

  useEffect(() => {
    if (!systemInfo.hasLogo) {
      navigate('/streaming');
      return;
    }

    fetchLogos(t).then(() => {
      //setLoading(false);
      triggerEvent('logo-fetched');
    });
    fetchLogoSettings(t).then(() => {
      setLoading(false);
    });
  }, []);

  const shouldReloadData = () => {
    fetchLogoSettings(t);
    fetchLogos(t).then(() => {
      //
    });
  };

  watchEvent('preset-load', () => {
    shouldReloadData();
  });

  const [uuid] = useMutable(uuidv4());
  let listController: ListSelector<LogoModel> = null;
  listController = createListStore(logoStore, uuid());

  const [importShown, showImport, hideImport] = useModalControls();

  const [configureShown, showConfigure, hideConfigure] = useModalControls();
  const [editing, setEditing] = useState<LogoSettingsViewModel>();
  const onEdit = (model: LogoSettingsViewModel, forceDisplayToTrue?: boolean) => {
    fetchLogoSettings(t).then(() => {
      const upToDate = logoSettingsStore.find((s) => s.interface === model.interface);
      const editModel = upToDate ?? model;
      if (forceDisplayToTrue === true) {
        editModel.display = true;
      }
      setEditing(editModel);
      showConfigure();
    });
  };
  const [selectAll, setSelectAll] = useState(false);
  const handleCheckAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      listController.selectAll();
    } else {
      listController.unselectAll();
    }
  };

  const onSortChanged = (k: any, order: any) => {
    // console.log(t, order);
    if (k === 'modifiedDate') {
      setSortState({ fieldKey: k, order: order, sortFieldKey: 'mtime' }); // workaround for NKE-2777 / HAIUI-2777
      return;
    }
    setSortState({ fieldKey: k, order: order });
  };

  // TODO improve placeholder
  const placeHolderColumnStructure = [
    { fieldKey: 'name', title: t('general.name') },
    { fieldKey: '', title: '', type: HaiDataTableColumnType.ACTIONS },
  ];
  const PlaceHolderPage = () => {
    return <Placeholder.Table columnStructure={placeHolderColumnStructure} numRows={3} showTitlebar />;
  };

  const onImport = (file: File) => {
    const printSuccessNotifications = false;
    return uploadLogo(file, t, printSuccessNotifications).then((res) => {
      if (res.ok) {
        //  hideImport();
        shouldReloadData();
      }
      return res;
    });
  };

  const onImportSuccess = () => {
    hideImport();
    shouldReloadData();
  };

  const onBulkDeleteProceed = (checkedFilesInView: LogoModel[]): any => {
    if (checkedFilesInView) {
      const promises = checkedFilesInView.map((itemToDelete) => {
        const item: LogoModel = logos.find((l) => l.name === itemToDelete.name);
        if (item == null) {
          return { ok: false, error: 'Failed to find logo' }; // should never happen
        }
        return deleteLogo(item, t);
      });

      return Promise.all(promises).then((response) => {
        let returnValue = 'error';
        const errors = response.filter((r) => !r.ok);
        const anyError = errors.length > 0;
        if (!anyError) {
          createAndDispatchNotification(
            t('logos.notifications.deleteSuccessMsg', { count: response.length }),
            NotificationVariant.SUCCESS,
          );
          returnValue = 'done';
        } else if (errors.length < response.length) {
          // some error and some success
          // error have already been printed individually
          createAndDispatchNotification(
            t('logos.notifications.deleteSuccessWithErrorsMsg', {
              count: response.length - errors.length,
              failures: errors.length,
            }),
            NotificationVariant.WARNING,
          );
        } else if (errors.length === response.length) {
          // error(s)
        }
        listController.reset();
        shouldReloadData();
        return returnValue;
      });
    }
    return null;
  };

  const [deleteShown, showDelete, hideDelete] = useModalControls();
  const onBulkDeleteClicked = (checkedFilesInView: unknown[]): any => {
    // console.log('onBulkDeleteClicked', checkedFilesInView)
    if (checkedFilesInView != null && checkedFilesInView.length < listController.list.length) {
      for (let i = 0; i < listController.list.length; i++) {
        const found = checkedFilesInView.find((item) => item === listController.list[i].name);
        if (found == null) {
          // unselect one item not in checkedFilesInView
          listController.unselect(listController.list[i]);
        }
      }
    }
    if (checkedFilesInView) {
      showDelete();
    }
  };
  const onSubmitConfigure = () => {
    hideConfigure();
    shouldReloadData();
  };

  const selectedMatrix = useStateWatch(listController, 'rowMatrix', listController.rowMatrix);

  const [logoView, setLogoView] = useLocalStorageWithState(
    `${constant.lStorage.logoView}:${sessionStore.username}`,
    'grid',
  );
  const onChangeView = (view: string) => {
    setLogoView(view);
    // listController.reset(); // clear selections. Buggy now because of HAIUI-1525
  };

  const filesData = logos.map((value, index: number) => {
    let associatedInterfaces = logoSettingsStore
      .filter((ls) => ls.fileName === value.name)
      .map((ls) => `${ls.interface + 1}`)
      .join(', ');
    if (associatedInterfaces && associatedInterfaces.length > 0) {
      associatedInterfaces = 'SDI ' + associatedInterfaces;
    }

    return {
      fileData: {
        id: value.name,
        name: value.name,
        nameLowerCase: value.name?.toLowerCase(),
        inUse: associatedInterfaces?.length ? associatedInterfaces : undefined,
        thumbnail:
          logoView === 'list' ? (
            <div className="squareThumbnail fs-mask">
              <img className="thumbnail" src={value.thumbnail as string} />
            </div>
          ) : (
            <img className="fs-mask" src={value.thumbnail as string} />
          ),

        mtime: value.mtime,
        modifiedDate: moment.unix(Number(value.mtime)).format(constant.momentFormat.logo),
        checked: selectedMatrix[index],
      },
      children: [],
    };
  });

  const proceedInlineDeleteSingle = (_eventKey: any, file: LogoModel) => {
    return onBulkDeleteProceed([file]);
  };

  const proceedDeleteSingle = (eventKey: any, file: IFileData | LogoModel) => {
    if (eventKey === 'show-delete') {
      return onBulkDeleteProceed([file as LogoModel]);
    }
    /*eslint no-console: 0*/
    console.warn('Unsupported action', eventKey);
  };

  const checkMultipleHandler = (ids: (number | string)[]) => {
    listController.list.forEach((item) => {
      if (ids.includes(item.id)) {
        listController.select(item);
      }
    });
  };

  const actionData /*: IFileExplorerActionItem[]*/ = [
    {
      title: t('general.delete'),
      actionIcon: 'TrashCan',
      showOnHover: false,
      eventKey: 'show-delete',
      className: 'primary-action-icon',
      iconColor: '#1E6FF1', // custom icon background color
      onConfirm: proceedDeleteSingle,
      inlineAction: {
        confirmationMessage: t('logos.deleteConfirmInline'),
        onConfirm: proceedInlineDeleteSingle,
        iconBackgroundColor: '#ea4645',
      },
    },
  ];

  const onSelectItem = (_checked: boolean, id: string) => {
    const logo = logos.find((l) => l.name === id);
    listController.toggleRow(logo);
  };

  const translations = {
    ...defaultLocaleInfo,
    ascendingText: t('logos.fileExplorer.ascendingText'),
    descendingText: t('logos.fileExplorer.descendingText'),
    sortOrderText: t('logos.fileExplorer.sortOrderText'),
    moveModalTitleText: t('logos.fileExplorer.moveModalTitleText'),
    moveModalCurrentLocationText: t('logos.fileExplorer.moveModalCurrentLocationText'),
    moveModalNewLocationText: t('logos.fileExplorer.moveModalNewLocationText'),
    moveModalCancelButtonText: t('logos.fileExplorer.moveModalCancelButtonText'),
    moveModalConfirmButtonText: t('logos.fileExplorer.moveModalConfirmButtonText'),
    deleteFilesDialogCancelText: t('logos.fileExplorer.deleteFilesDialogCancelText'),
    deleteFilesDialogConfirmText: t('logos.fileExplorer.deleteFilesDialogConfirmText'),
    deleteFilesDialogHeadlineText: t('logos.fileExplorer.deleteFilesDialogHeadlineText'),
    deleteFilesDialogBodyText: t('logos.fileExplorer.deleteFilesDialogBodyText'),
    deleteFileHeadlinePendingSingle: t('logos.fileExplorer.deleteFileHeadlinePendingSingle'),
    deleteFileBodyTextPending: t('logos.fileExplorer.deleteFileBodyTextPending'),
    deleteFileHeadlineSuccess: t('logos.fileExplorer.deleteFileHeadlineSuccess'),
    deleteFileHeadlineFail: t('logos.fileExplorer.deleteFileHeadlineFail'),
    deleteFileHeadlinePendingMulti: t('logos.fileExplorer.deleteFileHeadlinePendingMulti'),
    deleteFoldersWithFilesDialogCancelText: t('logos.fileExplorer.deleteFoldersWithFilesDialogCancelText'),
    deleteFoldersWithFilesDialogConfirmText: t('logos.fileExplorer.deleteFoldersWithFilesDialogConfirmText'),
    deleteFoldersWithFilesDialogHeadlineText: t('logos.fileExplorer.deleteFoldersWithFilesDialogHeadlineText'),
    deleteFoldersWithFilesDialogBodyText: t('logos.fileExplorer.deleteFoldersWithFilesDialogBodyText'),
    emptyStateHeadingText: t('logos.fileExplorer.emptyStateHeadingText'),
    emptyStateSubHeadingText: t('logos.fileExplorer.emptyStateSubHeadingText'),
    bulkActionDeleteButtonText: t('logos.fileExplorer.bulkActionDeleteButtonText'),
    bulkActionMoveButtonText: t('logos.fileExplorer.bulkActionMoveButtonText'),
  };

  return (
    <>
      <div className="mt-32px">
        <Heading4Med data-auto={generateDataAuto('title', 'logos')}>{t('logos.sections.title')}</Heading4Med>
        <>
          {loading ? (
            <PlaceHolderPage />
          ) : (
            <LogoSettingsForm models={logoSettings} onEdit={onEdit} shouldReloadData={shouldReloadData} />
          )}
        </>
        <span className="logos-file-explorer-header mt-4">
          <Heading4Med data-auto={generateDataAuto('title', 'logos')}>{t('logos.sections.logos')}</Heading4Med>
        </span>
        <div className="hai-p-0">
          {loading ? (
            <>
              <br />
              <PlaceHolderPage />
            </>
          ) : (
            <div id="logos-table">
              <FileExplorer
                allowSortNone={false}
                disableDoubleClick={true}
                selectable={!sessionStore.isUser()}
                actionItems={!sessionStore.isUser() && actionData}
                checkAll={selectAll}
                currentFolder={filesData}
                dataStructure={[
                  {
                    fieldKey: 'name',
                    title: 'Name',
                    useFsMask: true,
                    type: HaiDataTableColumnType.CUSTOM,
                    sortFieldKey: 'nameLowerCase',
                    secondLevelSortKey: true,
                  },
                  { fieldKey: 'inUse', title: 'In Use', useAsGridTitle: true },
                  {
                    fieldKey: 'modifiedDate',
                    title: 'Modified Date',
                    useAsGridTitle: true,
                    sortFieldKey: 'mtime',
                  },
                  { fieldKey: '', title: '', type: HaiDataTableColumnType.ACTIONS },
                ]}
                initialSort={sortStateState}
                localeInfo={translations}
                viewType={logoView}
                onThumbnailClick={
                  sessionStore.isUser()
                    ? undefined
                    : (id: any, event: any) => {
                        const item = logos.find((l) => l.name === id);
                        listController.toggleRow(item);
                        if (logoView === 'grid') {
                          event.preventDefault(); // Required for NKE-3184 / HAIUI-2036
                        }
                      }
                }
                // if we want haiui to handle the delete modal onBulkDelete={onBulkDelete}
                onBulkDeleteClicked={onBulkDeleteClicked}
                onCheckAll={handleCheckAll}
                onCheckMultiple={checkMultipleHandler}
                onSelect={onSelectItem}
                onSort={onSortChanged}
                onToggleView={onChangeView}
              >
                <FileExplorer.Navigation>
                  <FileExplorer.BreadCrumb />
                  <FileExplorer.ActionGroup allowCreateNewFolder={false}>
                    {!sessionStore.isUser() && (
                      <Button variant="primary" onClick={showImport}>
                        {t('logos.button.add')}
                      </Button>
                    )}
                  </FileExplorer.ActionGroup>
                </FileExplorer.Navigation>
              </FileExplorer>
            </div>
          )}
        </div>
      </div>
      {importShown && <LogoImportModal onCancel={hideImport} onImport={onImport} onImportSuccess={onImportSuccess} />}
      {configureShown && <LogoConfigureModal onSubmit={onSubmitConfigure} onCancel={hideConfigure} model={editing} />}
      {deleteShown && (
        <GenericDeleteModal
          title="logos.deleteModal.title"
          // if we want transition titleInProgress="logos.deleteModal.titleInProgress"
          itemsToDelete={listController.selectedRows()}
          onCancel={() => {
            hideDelete();
          }}
          onDelete={onBulkDeleteProceed}
          onSuccess={hideDelete}
        />
      )}
    </>
  );
};
